import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  showConfirmationPrompt,
  showDialog,
  showSuccessToast,
} from '@bpinternal/ui-kit-next'
import { useNavigate } from '@tanstack/react-router'
import {
  AppWindow,
  Copy,
  Ellipsis,
  FileIcon,
  Folder,
  LucideIcon,
  MoreVertical,
  Pencil,
  Trash,
  TypeIcon,
} from 'lucide-react'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import darkFolderIcon from '~/assets/folder-dark.svg?react'
import folderIcon from '~/assets/folder.svg?react'
import { Icon, Spinner } from '~/elements'
import { useDeleteKnowledgeBase } from '~/features/knowledge-bases/hooks/useKnowledgeBase'
import { useKnowledgeSource, useListWebPageSourceDsIds } from '~/features/knowledge-bases/hooks/useKnowledgeSource'
import { useLocalStore } from '~/stores'
import { Color } from '~/types'
import { cn } from '~/utils/cn'
import { copyToClipboard } from '~/utils/copy-to-clipboard'
import { KnowledgeBaseDropdownItem, KnowledgeBaseSummary } from '../types'
import { RenameKBDialog } from './RenameKBDialog'

type KnowledgeBaseCardProps = {
  workspaceId: string
  botId: string
} & KnowledgeBaseSummary

type KnowledgeBaseSourceItem = {
  count: number
  label: string
  icon: LucideIcon
  color: Color
  hasNextPage?: boolean
  isFetching?: boolean
}

export const KnowledgeBaseCard = ({ id, name, createdAt, workspaceId, botId, oldKbId }: KnowledgeBaseCardProps) => {
  const [desktopOpen, setDesktopOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [isHover, setIsHover] = useState(false)
  const kbId = oldKbId || id

  const {
    data: documentData,
    fetchNextPage: fetchNextDocumentPage,
    hasNextPage: hasNextDocumentPage,
    isFetching: isFetchingDocument,
  } = useKnowledgeSource({ workspaceId, botId, kbId, dsType: 'document' })
  const {
    data: richTextData,
    fetchNextPage: fetchNextRichTextPage,
    hasNextPage: hasNextRichTextPage,
    isFetching: isFetchingRichText,
  } = useKnowledgeSource({ workspaceId, botId, kbId, dsType: 'rich-text' })
  const theme = useLocalStore((s) => s.getPreferredTheme())
  const dsIds = useListWebPageSourceDsIds({ workspaceId, botId, kbId })
  const documentSources = documentData.pages.flatMap((page) => page.files)
  const richTextSources = richTextData.pages.flatMap((page) => page.files)

  const knowledgeSources = [...documentSources, ...richTextSources]
  const hasNextPage = hasNextDocumentPage || hasNextRichTextPage
  const isFetching = isFetchingDocument || isFetchingRichText

  const MAX_PAGES = 5

  useEffect(() => {
    if (hasNextDocumentPage && documentData.pages.length < MAX_PAGES) {
      void fetchNextDocumentPage()
    }
    if (hasNextRichTextPage && richTextData.pages.length < MAX_PAGES) {
      void fetchNextRichTextPage()
    }
  }, [
    documentData.pages.length,
    fetchNextDocumentPage,
    hasNextDocumentPage,
    richTextData.pages.length,
    fetchNextRichTextPage,
    hasNextRichTextPage,
  ])

  const { mutate: deleteKnowledgeBase } = useDeleteKnowledgeBase()
  const navigate = useNavigate()

  const createDropdownActions = (onClose: () => void): KnowledgeBaseDropdownItem[] => [
    {
      label: 'Rename',
      icon: Pencil,
      onClick: () => {
        onClose()
        showDialog(
          ({ close }) => (
            <RenameKBDialog kbId={id} close={close} workspaceId={workspaceId} botId={botId} oldName={name} />
          ),
          {
            title: 'Rename Knowledge Base',
          }
        )
      },
    },
    {
      label: 'Copy ID',
      icon: Copy,
      onClick: () => {
        copyToClipboard(kbId, 'Knowledge Base ID')
      },
    },
    {
      label: 'Delete',
      icon: Trash,
      destructive: true,
      onClick: () => {
        onClose()
        showConfirmationPrompt('Are you sure you want to delete this knowledge base ?', {
          title: 'Delete Knowledge Base',
          variant: 'danger',
        }).then(async () => {
          deleteKnowledgeBase({
            id: kbId,
            workspaceId,
            botId,
            onSuccess: () => {
              showSuccessToast('Knowledge base deleted successfully')
            },
          })
        })
      },
    },
  ]

  const knowledgeSourceItems: KnowledgeBaseSourceItem[] = [
    {
      count: documentSources.length,
      label: 'Document',
      icon: FileIcon,
      color: 'amber',
      hasNextPage: hasNextDocumentPage,
      isFetching: isFetchingDocument,
    },
    {
      count: richTextSources.length,
      label: 'Inline Document',
      icon: TypeIcon,
      color: 'pink',
      hasNextPage: hasNextRichTextPage,
      isFetching: isFetchingRichText,
    },
    {
      count: dsIds.length,
      label: 'Website',
      icon: AppWindow,
      color: 'blue',
    },
  ]

  return (
    <>
      {/* Desktop Card */}
      <Card
        className="hidden w-full cursor-pointer flex-col md:flex"
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onClick={() =>
          navigate({
            to: '/workspaces/$workspaceId/bots/$botId/knowledge-bases/$kbId',
            params: { workspaceId, botId, kbId: id },
          })
        }
      >
        <CardHeader className="p-4">
          <CardDescription className="font-medium text-foreground">
            {isFetching ? (
              <div className="flex items-center gap-2">
                <Spinner size="2" />
                <p className="">Fetching {knowledgeSources.length + dsIds.length === 1 ? 'Item' : 'Items'}...</p>
              </div>
            ) : (
              `${knowledgeSources.length + dsIds.length} ${hasNextPage ? '+' : ''} ${knowledgeSources.length + dsIds.length === 1 ? 'Item' : 'Items'}`
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex min-h-[144px] w-full flex-1 items-center justify-center">
          <div
            className={cn(
              'z-10 flex h-full w-full items-center justify-center opacity-0 transition-all duration-300 ease-out',
              isHover && 'opacity-100 backdrop-blur-sm duration-200 ease-in'
            )}
          >
            {knowledgeSources.length > 0 || dsIds.length > 0 ? (
              <div className="z-10 grid h-full w-full grid-cols-2">
                {knowledgeSourceItems.map(
                  (item) => item.count > 0 && <KnowledgeBaseSourceItem key={item.label} {...item} />
                )}
              </div>
            ) : (
              <p className="text-center text-muted-foreground">No Data sources</p>
            )}
          </div>

          <Icon icon={theme === 'dark' ? darkFolderIcon : folderIcon} className="absolute !size-28" />
        </CardContent>
        <CardFooter className="flex-row items-start justify-between rounded-b-xl border-t border-border bg-neutral-50 p-4">
          <div className="flex min-w-0 flex-col gap-2">
            <CardTitle className="truncate text-base font-medium text-foreground">{name}</CardTitle>
            <div className="flex flex-row gap-2.5 text-sm text-muted-foreground">
              <span className="text-sm font-medium">Created </span>
              <p>{DateTime.fromISO(createdAt).toRelative()}</p>
            </div>
          </div>

          <KnowledgeBaseDropdownMenu
            actions={createDropdownActions(() => setDesktopOpen(false))}
            open={desktopOpen}
            onOpenChange={setDesktopOpen}
            variant="desktop"
          />
        </CardFooter>
      </Card>
      {/* Mobile Card */}
      <Card
        className="flex w-full min-w-0 flex-col md:hidden"
        onClick={() =>
          navigate({
            to: '/workspaces/$workspaceId/bots/$botId/knowledge-bases/$kbId',
            params: { workspaceId, botId, kbId: id },
          })
        }
      >
        <CardHeader className="flex flex-row items-center justify-between p-4">
          <div className="flex-0 flex min-w-0 max-w-full items-center gap-2">
            <Folder className="flex-none" />
            <CardTitle className="truncate font-medium text-foreground">{name}</CardTitle>
          </div>
          <KnowledgeBaseDropdownMenu
            actions={createDropdownActions(() => setMobileOpen(false))}
            open={mobileOpen}
            onOpenChange={setMobileOpen}
            variant="mobile"
          />
        </CardHeader>
        <CardContent className="flex flex-col gap-4 p-4 pt-0">
          <CardDescription className="text-sm font-medium text-muted-foreground">
            {knowledgeSources.length + dsIds.length} {knowledgeSources.length + dsIds.length === 1 ? 'Item' : 'Items'}
          </CardDescription>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-medium">Created </span>
            <p>{DateTime.fromISO(createdAt).toRelative()}</p>
          </div>
        </CardContent>
      </Card>
    </>
  )
}

const KnowledgeBaseDropdownMenu = ({
  actions,
  open,
  onOpenChange,
  variant = 'desktop',
}: {
  actions: KnowledgeBaseDropdownItem[]
  open: boolean
  onOpenChange: (open: boolean) => void
  variant?: 'mobile' | 'desktop'
}) => {
  const Icon = variant === 'mobile' ? Ellipsis : MoreVertical

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" className="flex-shrink-0 [&_svg]:!size-6">
          <Icon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((action, index) => (
          <KnowledgeBaseDropdownMenuItem action={action} key={index} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const KnowledgeBaseDropdownMenuItem = ({ action }: { action: KnowledgeBaseDropdownItem }) => {
  return (
    <DropdownMenuItem
      onClick={(e) => {
        e.stopPropagation()
        action.onClick()
      }}
      className={cn(
        'flex justify-between',
        action.destructive && 'text-danger-600 focus:bg-danger-100 focus:text-danger-700'
      )}
    >
      <span>{action.label}</span>
      {action.icon && <action.icon className={cn(action.destructive && 'stroke-danger-600 focus:stroke-danger-700')} />}
    </DropdownMenuItem>
  )
}

const KnowledgeBaseSourceItem = ({ icon, count, label, color, hasNextPage, isFetching }: KnowledgeBaseSourceItem) => {
  return (
    <>
      {isFetching ? (
        <div key={label} className="flex items-center gap-2">
          <Spinner size="2" />
          <p className="text-sm text-muted-foreground">Fetching {label}s...</p>
        </div>
      ) : (
        <div key={label} className="flex items-center gap-2">
          <Icon icon={icon} color={color} variant="soft" size="3" />
          <p className="text-sm text-muted-foreground">
            {count}
            {hasNextPage && label !== 'Website' && '+'} {count === 1 ? label : label + 's'}
          </p>
        </div>
      )}
    </>
  )
}
